import { Button, Col, Input, Row } from "antd";
import { useStoreFormMutation } from "api/TenantSlug/Form/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { CategorySelectSearchable } from "components/UI/Selects/CategorySelectSearchable/CategorySelectSearchable.tsx";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { TOAST_TYPES } from "localConstants";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { showCustomToast } from "utils/toast";

import { FormDesignAddFormData, schema } from "./formSchema";

import StoreFormRequest = App.Http.Requests.Form.StoreFormRequest;
interface FormDesignAddFormProps {
    onSubmit?: () => void;
}

const FormDesignAddFormContent: FC<FormDesignAddFormProps> = ({ onSubmit }) => {
    const { t } = usePaldockTranslation("forms.form-design-add");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const generalFormUtils = useGeneralForm<FormDesignAddFormData>();
    const { control, fieldNames, handleSubmit } = generalFormUtils;

    const userUtils = useUser();
    const storeFormMutation = useStoreFormMutation();

    const onSubmitForm = (values: FormDesignAddFormData) => {
        storeFormMutation.mutate(
            {
                form_structure_id: values.form_structure_id.toString(),
                name: values.name,
                category_id: values.category ?? null,
            } as StoreFormRequest,
            {
                onSuccess: (response) => {
                    if (response?.form?.id) {
                        showCustomToast(
                            {
                                title: t("messages.add_success"),
                                buttons: [
                                    {
                                        label: tC("open"),
                                        href: response?.form?.id
                                            ? `/${userUtils.getActiveTenant()?.slug_name}/tool/design/form/${response.form.id}`
                                            : undefined,
                                    },
                                ],
                            },
                            TOAST_TYPES.SUCCESS
                        );
                        onSubmit?.();
                    }
                },
                onError: (error) => {
                    toast.error(t("messages.add_error"));
                    generalFormUtils.handleErrorsFromErrorResponse(error);
                },
            }
        );
    };

    return (
        <Row gutter={16}>
            <Col span={24}>
                <FormItem control={control} name={fieldNames.name.name} label={tFields("name.label")} required>
                    <Input placeholder={tFields("name.placeholder")} />
                </FormItem>
            </Col>
            <Col span={24}>
                <FormItem
                    control={control}
                    name={fieldNames.form_structure_id.name}
                    label={tFields("form_structure.label")}
                    required
                >
                    <FormStructureSelectSearchable />
                </FormItem>
            </Col>
            <Col span={24}>
                <FormItem
                    control={control}
                    name={fieldNames.category?.name || "category"}
                    label={tFields("category.label")}
                >
                    <CategorySelectSearchable allowClear />
                </FormItem>
            </Col>
            <Col span={24}>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={storeFormMutation.isPending}
                    onClick={handleSubmit(onSubmitForm)}
                >
                    {tC("add")}
                </Button>
            </Col>
        </Row>
    );
};

export const FormDesignAddForm: FC<FormDesignAddFormProps> = ({ onSubmit }) => {
    const defaultValues = {
        name: "",
        form_structure_id: "",
        category: null,
    };

    return (
        <div className="max-w-xl">
            <GeneralFormProvider<FormDesignAddFormData> schema={schema} defaultValues={defaultValues}>
                <FormDesignAddFormContent onSubmit={onSubmit} />
            </GeneralFormProvider>
        </div>
    );
};
